import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable link component styled as a button, displaying an edit icon (FaEdit).
 * Uses react-router-dom's Link component for navigation and includes a key prop.
 * Uses semantic styling and configuration support.
 *
 * @param {Object} props - Component props
 * @param {string} [props.editPath='#'] - The path to navigate to on click.
 * @param {string} [props.title='Edit'] - The link's title and aria-label text.
 * @param {string} [props.className=''] - Additional CSS classes for styling.
 * @param {string} [props.keyValue=""] - The key value for the Link component.
 * @param {boolean} [props.showText=false] - Whether to display the title text next to the icon.
 * @param {boolean} [props.disabled=false] - Whether the link is disabled (adds disabled attribute).
 * @returns {JSX.Element} The rendered EditLinkButtonWithKey component.
 */
const EditLinkButtonWithKey = ({ 
    editPath = '#', 
    title = 'Edit', 
    className = '', 
    keyValue = '', 
    showText = false, 
    disabled = false
}) => {
    const { replace } = useTextReplacement();

    return (
        <Link
            to={editPath}
            key={keyValue}
            className={`p-2 rounded-full cta-button-primary transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            disabled={disabled}
            aria-label={replace(title)}
            title={replace(title)}
        >
            <FaEdit /> {showText && <span className="ml-1">{replace(title)}</span>}
        </Link>
    );
};

EditLinkButtonWithKey.propTypes = {
    editPath: PropTypes.string,
    title: PropTypes.string,
    className: PropTypes.string,
    keyValue: PropTypes.string,
    showText: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default EditLinkButtonWithKey;

